import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link, Stack, SxProps, Typography, useTheme } from "@mui/material";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import { TypographyOwnProps, TypographyProps } from "@mui/material/Typography";
import useDownloadFile from "src/Pages/Company/Absences/api/useDownloadFile";
import __ from "src/utils/Translations";

export interface IFileItem {
  attachmentId: string;
  fileName: string;
  fileSize?: string;
  fileExtension?: string;
}

interface IFileListProps {
  scopeId: string;
  title: string;
  data: IFileItem[];
  downloadLabel: string;
  customLinkColor?: string;
  customContainerSx?: SxProps;
  customTitleStyle?: TypographyProps & TypographyOwnProps;
  customParams?: Record<string, string>;
  shortVersionLink?: string | null;
  isShortVersion?: boolean;
}
const FileList: FC<IFileListProps> = ({
  scopeId,
  title,
  data,
  downloadLabel,
  customContainerSx,
  customLinkColor,
  customTitleStyle,
  customParams,
  shortVersionLink,
}) => {
  const theme = useTheme();
  const dowloadFile = useDownloadFile();

  return (
    <>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{ marginBottom: "1rem" }}
        {...customTitleStyle}
      >
        {title}
      </Typography>
      <Stack
        component="ul"
        direction="column"
        spacing="1rem"
        sx={{ padding: 0, ...customContainerSx }}
      >
        {data.map((file) => (
          <Stack
            key={file.attachmentId}
            component="li"
            direction="row"
            spacing={1}
            sx={{ alignItems: "center" }}
          >
            <UploadFileOutlined color="primary" />
            <Typography
              variant="body2"
              component="span"
              color="text.primary"
              sx={{ flex: "0 0 auto", maxWidth: { xs: "200px", md: "none" } }}
            >
              {file.fileName}
              {file.fileExtension && `.${file.fileExtension}`}
              {file?.fileSize && (
                <>
                  <Typography
                    variant="body2"
                    component="span"
                    color="text.secondary"
                    sx={{ mx: 1 }}
                  >
                    •
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    color="text.secondary"
                    sx={{ flex: "0 0 auto" }}
                  >
                    {file.fileSize}
                  </Typography>
                </>
              )}
            </Typography>
            <Typography
              variant="body2"
              component="span"
              color="text.secondary"
              sx={{ display: { xs: "none", md: "block" } }}
            >
              •
            </Typography>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              variant="body2"
              component="button"
              sx={{
                flex: { xs: 1, md: "0 0 auto" },
                textAlign: { xs: "end", md: "center" },
              }}
              color={customLinkColor || "text.secondary"}
              onClick={() =>
                dowloadFile(
                  {
                    scopeId,
                    attachmentId: file.attachmentId,
                    ...(customParams ?? {}),
                  },
                  file.fileName
                )
              }
            >
              {downloadLabel}
            </Link>
          </Stack>
        ))}
      </Stack>
      {shortVersionLink && (
        <RouterLink
          color="text.primary"
          to={shortVersionLink}
          style={{ color: theme.palette.text.primary, width: "fit-content" }}
        >
          <Typography variant="body1">
            {__("Zobacz wszystkie załączniki")}
          </Typography>
        </RouterLink>
      )}
    </>
  );
};

export default FileList;
